import { useContext, useEffect } from "react"
import ViewportDebugPage from "./pages/ViewportDebugPage"
import { noteViewportRoute } from "./lib/viewportLog"
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom"
import { useState } from "react"
import LoginPage from "./pages/LoginPage"
import FinishSignupPage from "./pages/FinishSignupPage"
import AccountPage from "./pages/AccountPage"
import { UserContext, UserProvider } from "./components/UserContext"
import Spinner from "./components/Spinner"
import ConfigPage from "./pages/ConfigPage"
import NavigationBar from "./components/NavigationBar"
import FeatureDetailPage from "./pages/FeatureDetailPage"
import CheckinsPage from "./pages/CheckinsPage"
import SquadPage from "./pages/SquadPage"
import AllAdvisorsPage from "./pages/AllAdvisorsPage"
import AdminConsole from "./pages/AdminConsole"
import LlmUsagePage from "./pages/LlmUsagePage"
import ParseQualityPage from "./pages/ParseQualityPage"
import TestTrackerPage from "./pages/TestTrackerPage"
import JobsPage from "./pages/JobsPage"
import AdminUserPage from "./pages/AdminUserPage"
import AdviceChatPage from "./pages/AdviceChatPage"
import ProjectsPage from "./pages/ProjectsPage"
import ProjectDetailPage from "./pages/ProjectDetailPage"
import GenieChat from "./pages/GenieChat"
import GenieHistory from "./pages/GenieHistory"
import ChatsPage from "./pages/ChatsPage"
import ChatInspectorPage from "./pages/ChatInspectorPage"
import AdvicePage from "./pages/AdvicePage"
import CollectionsPage from "./pages/CollectionsPage"
import CollectionDetailPage from "./pages/CollectionDetailPage"
import DashboardPage from "./pages/DashboardPage"
import PageErrorBoundary from "./components/PageErrorBoundary"
import { NoticeProvider } from "./components/notice/NoticeProvider"
import { syncPushRegistration } from "./lib/pushRegistration"
import { onAppUrlOpen } from "./lib/nativeShell"

const App = () => {
    return (
        <UserProvider>
            <NoticeProvider>
                <BrowserRouter>
                    <AppContent />
                </BrowserRouter>
            </NoticeProvider>
        </UserProvider>
    )
}


const AppContent = () => {
    const { user, refreshUser } = useContext(UserContext)
    const [loading, setLoading] = useState(true)
    const location = useLocation()
    const navigate = useNavigate()
    
    useEffect(() => {
        if (user) {
            return
        }
        const checkSession = async () => {
            try {
                await refreshUser()
            } finally {
                setLoading(false)
            }
        }
        checkSession()
    }, [])

    useEffect(() => {
        noteViewportRoute(location.pathname)
    }, [location.pathname])

    // In the app shell, keep the backend's push token current on each open
    // (APNs tokens rotate). Registers only where push is already granted;
    // never prompts. A browser, or a shell without the plugin, does nothing.
    useEffect(() => {
        if (!user) return
        void syncPushRegistration()
    }, [user?.id])

    // In the app shell, a universal link (today: a login link tapped in the
    // mail app) opens the app with the URL, which iOS hands to the page rather
    // than loading it. The link's host is ignored: every deployment serves
    // this same app on the same database, so a production link consumed by a
    // shell pointed at staging or a clone signs in all the same. An /api/ path
    // needs a real navigation — the backend sets the cookie and redirects —
    // and anything else is a route.
    useEffect(() => onAppUrlOpen((url) => {
        let target: URL
        try {
            target = new URL(url)
        } catch {
            return
        }
        const path = target.pathname + target.search + target.hash
        if (target.pathname.startsWith("/api/")) {
            window.location.assign(path)
        } else {
            navigate(path)
        }
    }), [navigate])

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spinner />
            </div>
        )
    }
    
    return (
        <div className="min-h-screen bg-blush-light">
            {/* Signed-out visitors only ever see the login and signup pages,
                which carry the name themselves; the bar would offer nothing
                but a link to the page they are on. Without it, the page takes
                over the bar's status-bar inset in the app shell. */}
            {user && <NavigationBar />}
            {/* The dashboard escapes the shared container so its activity
                landscape can run full-bleed, flush under the nav; it applies
                the container to its other sections itself. */}
            <main className={`${location.pathname === '/dashboard' ? '' : 'max-w-7xl mx-auto py-4 px-0 sm:px-6 lg:px-8'}${user ? '' : ' shell-safe-top'}`}>
                <PageErrorBoundary resetKey={location.pathname}>
                <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<CheckinsPage />} />

                    {/* Login -- can't be authed */}
                    {!user && <Route path="/login" element={<LoginPage />} />}
                    {!user && <Route path="/finish-signup" element={<FinishSignupPage />} />}
                    {/* Finishing sign-up is every account's first sign-in, and
                        it starts in Chats. A route rather than a navigate()
                        after refreshUser: React Router applies navigations as
                        transitions, so the signed-in render lands first, finds
                        no /finish-signup route, and the catch-all wins. */}
                    {user && <Route path="/finish-signup" element={<Navigate to="/chats" replace />} />}

                    {/* Authentication-requiring routes */}
                    {user && <>
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/account" element={<AccountPage />} />

                        {/* Projects routes */}
                        <Route path="/projects" element={<ProjectsPage />} />
                        <Route path="/projects/:projectId" element={<ProjectDetailPage />} />

                        {/* Features routes */}
                        <Route path="/features/:featureId" element={<FeatureDetailPage />} />
                        <Route path="/checkins" element={<CheckinsPage />} />

                        {/* Collections routes */}
                        <Route path="/collections" element={<CollectionsPage />} />
                        <Route path="/collections/:collectionId" element={<CollectionDetailPage />} />
                        
                        <Route path="/squad" element={<SquadPage />} />
                        <Route path="/advice" element={<AdvicePage />} />
                        <Route path="/advice/:chatId" element={<AdviceChatPage />} />
                        <Route path="/genie/history" element={<GenieHistory />} />
                        <Route path="/genie/:chatId?" element={<GenieChat />} />
                        <Route path="/chats/:conversationId?" element={<ChatsPage />} />
                    </>}

                    {/* Admin-only routes */}
                    {user?.isAdmin && (
                        <>
                            <Route path="/admin" element={<AdminConsole />} />
                            <Route path="/admin/llm-usage" element={<LlmUsagePage />} />
                            <Route path="/admin/parse-quality" element={<ParseQualityPage />} />
                            <Route path="/admin/tests" element={<TestTrackerPage />} />
                            <Route path="/admin/jobs" element={<JobsPage />} />
                            <Route path="/admin/viewport" element={<ViewportDebugPage />} />
                            <Route path="/admin/chats/:conversationId?" element={<ChatInspectorPage />} />
                            <Route path="/admin/users" element={<AdminUserPage />} />
                            <Route path="/admin/users/:userId" element={<AdminUserPage />} />
                            <Route path="/config" element={<ConfigPage />} />
                            <Route path="/advisors" element={<AllAdvisorsPage />} />
                        </>
                    )}

                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
                </PageErrorBoundary>
            </main>
        </div>
    )
}

export default App